import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  CellStyleFunc,
  ValueGetterParams,
} from 'ag-grid-community'
import { Horizontal, Texto, MaterialIcon } from '@gravitate-js/excalibrr'
import type { Match } from '../../../../../../api/useSupplierReconciliation/types/match'
import { formatters } from '../../../../../../utils/intl/dateTimeFormatters'
import { createDateStatusDualColumnFilter, createDateStatusDualColumnFilterParams } from '../utils/filterUtils'
import moment from 'moment'
import { formatInvoiceDate } from '../../../Detail/columnDefs/detailColumnDefs/utils/formatters'
import { getDefaultTimeFilterParams, getComparator } from '@/utils/grid'

interface DateColumnsOptions {
  getTimezoneForMatch?: (terminals?: string[]) => string | undefined
}

export function getDateColumns(options: DateColumnsOptions = {}): ColDef<Match>[] {
  const { getTimezoneForMatch } = options
  return [
    // Days Until Due Date column (primary date column)
    {
      colId: 'days_until_due',
      headerName: 'DAYS UNTIL DUE',
      flex: 1,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.invoice_due_date) return null

        const dueDate = new Date(params.data.invoice_due_date)
        const currentDate = new Date()

        dueDate.setHours(0, 0, 0, 0)
        currentDate.setHours(0, 0, 0, 0)

        const timeDiff = dueDate.getTime() - currentDate.getTime()
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))

        return daysDiff
      },
      cellRenderer: (params: ICellRendererParams) => {
        if (params.value === null || params.value === undefined) {
          return (
            <Texto appearance='hint' align='center'>
              N/A
            </Texto>
          )
        }

        const days = params.value
        let appearance: 'default' | 'error' | 'warning' | 'success' = 'default'
        let icon = null

        if (days < 0) {
          appearance = 'error'
          icon = <MaterialIcon icon='schedule' style={{ color: 'var(--theme-error)', fontSize: 14 }} />
        } else if (days <= 3) {
          appearance = 'warning'
          icon = <MaterialIcon icon='schedule' style={{ color: 'var(--theme-warning)', fontSize: 14 }} />
        } else if (days <= 7) {
          appearance = 'default'
          icon = <MaterialIcon icon='schedule' style={{ color: 'var(--theme-text-secondary)', fontSize: 14 }} />
        } else {
          appearance = 'success'
          icon = <MaterialIcon icon='schedule' style={{ color: 'var(--theme-success)', fontSize: 14 }} />
        }

        return (
          <Horizontal verticalCenter gap={4} style={{ justifyContent: 'center' }}>
            {icon}
            <Texto appearance={appearance} weight={days <= 3 ? 'bold' : 'normal'} align='center'>
              {days}
            </Texto>
          </Horizontal>
        )
      },
      cellStyle: ((params: ICellRendererParams<Match>) => {
        if (params.value === null || params.value === undefined) return null

        const days = params.value
        if (days < 0) {
          return { backgroundColor: 'var(--theme-error-dim)', color: 'var(--theme-error)' }
        } else if (days <= 3) {
          return { backgroundColor: 'var(--theme-warning-dim)', color: 'var(--theme-warning)' }
        }
        return null
      }) as CellStyleFunc<Match>,
    },
    {
      colId: 'bol_date',
      headerName: 'BOL DATE',
      field: 'bol_date',
      flex: 1,
      filter: createDateStatusDualColumnFilter({
        field: 'bol_date',
        dateFieldTitle: 'Filter By BOL Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.bol_date) return 'Missing'
          return 'Attached'
        },
      }),
      filterParams: createDateStatusDualColumnFilterParams({
        field: 'bol_date',
        dateFieldTitle: 'Filter By BOL Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.bol_date) return 'Missing'
          return 'Attached'
        },
      }),
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.bol_date) return null
        // Parse the date and normalize to midnight for consistent filtering
        const date = new Date(params.data.bol_date)
        date.setHours(0, 0, 0, 0)
        return date
      },
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data?.bol_date) {
          return <Texto appearance='hint'>N/A</Texto>
        }
        const timezone =
          params.data.bol_date_local ||
          params.data.timezone_data?.terminal ||
          (getTimezoneForMatch ? getTimezoneForMatch(params.data.terminals) : undefined)

        const displayValue = formatters.DATE(moment.utc(params.data.bol_date), timezone, false)
        return <Texto>{displayValue}</Texto>
      },
    },
    {
      field: 'bol_date',
      headerName: 'BOL TIME',
      flex: 1,
      filter: 'agTextColumnFilter',
      comparator: (a, b) => getComparator(a, b, 'time'),
      valueGetter: (params) => {
        const date = params.data?.bol_date
        const tz = params.data?.bol_date_local
        return date ? formatters.TIME(date, tz) : ''
      },
      filterParams: getDefaultTimeFilterParams(),
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) {
          return <Texto appearance='hint'>N/A</Texto>
        }
        return <Texto>{params.value}</Texto>
      },
    },
    {
      colId: 'invoice_date',
      headerName: 'INVOICE DATE',
      field: 'invoice_date',
      flex: 1,
      hide: true,
      filter: createDateStatusDualColumnFilter({
        field: 'invoice_date',
        dateFieldTitle: 'Filter By Invoice Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.invoice_date) return 'Missing'
          return 'Attached'
        },
      }),
      filterParams: createDateStatusDualColumnFilterParams({
        field: 'invoice_date',
        dateFieldTitle: 'Filter By Invoice Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.invoice_date) return 'Missing'
          return 'Attached'
        },
      }),
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.invoice_date) return null
        // Parse the date and normalize to midnight for consistent filtering
        const date = new Date(params.data.invoice_date)
        date.setHours(0, 0, 0, 0)
        return date
      },
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data?.invoice_date) {
          return <Texto appearance='hint'>N/A</Texto>
        }

        // Use new API timezone data following Order Movements pattern
        // Priority: timezone_data.terminal from API data, then fallback
        const timezone =
          params.data.timezone_data?.terminal ||
          (getTimezoneForMatch ? getTimezoneForMatch(params.data.terminals) : undefined)

        // CRITICAL: Parse as UTC first (like createTimezoneAwareDateDef does)
        // API returns UTC timestamps without 'Z' suffix, so we must parse as UTC
        // Use false for overrideSelectedTime to respect Control Panel settings
        const displayValue = formatters.DATE_TIME(moment.utc(params.data.invoice_date), timezone, false)

        return <Texto>{displayValue}</Texto>
      },
    },
    {
      colId: 'invoice_bol_date',
      headerName: 'INVOICE BOL DATE',
      field: 'invoice_bol_date',
      flex: 1,
      filter: createDateStatusDualColumnFilter({
        field: 'invoice_bol_date',
        dateFieldTitle: 'Filter By Invoice BOL Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.invoice_bol_date) return 'Missing'
          return 'Attached'
        },
      }),
      filterParams: createDateStatusDualColumnFilterParams({
        field: 'invoice_bol_date',
        dateFieldTitle: 'Filter By Invoice BOL Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.invoice_bol_date) return 'Missing'
          return 'Attached'
        },
      }),
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.invoice_bol_date) return null
        // Parse the date and normalize to midnight for consistent filtering
        const date = new Date(params.data.invoice_bol_date)
        date.setHours(0, 0, 0, 0)
        return date
      },
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data?.invoice_bol_date) {
          return <Texto appearance='hint'>N/A</Texto>
        }
        const timezone =
          params.data.timezone_data?.terminal ||
          (getTimezoneForMatch ? getTimezoneForMatch(params.data.terminals) : undefined)
        const displayValue = formatInvoiceDate(params.data.invoice_bol_date, timezone)

        return <Texto>{displayValue}</Texto>
      },
    },
    {
      field: 'invoice_bol_date',
      headerName: 'INVOICE BOL TIME',
      flex: 1,
      filter: 'agTextColumnFilter',
      comparator: (a, b) => getComparator(a, b, 'time'),
      valueGetter: (params) => {
        const date = params.data?.invoice_bol_date
        const tz = params.data?.bol_date_local
        return date ? formatters.TIME(date, tz) : ''
      },
      filterParams: getDefaultTimeFilterParams(),
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) {
          return <Texto appearance='hint'>N/A</Texto>
        }
        return <Texto>{params.value}</Texto>
      },
    },
    {
      colId: 'invoice_due_date',
      headerName: 'INVOICE DUE',
      field: 'invoice_due_date',
      flex: 1,
      filter: createDateStatusDualColumnFilter({
        field: 'invoice_due_date',
        dateFieldTitle: 'Filter By Invoice Due Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.invoice_due_date) return 'Missing'
          return 'Attached'
        },
      }),
      filterParams: createDateStatusDualColumnFilterParams({
        field: 'invoice_due_date',
        dateFieldTitle: 'Filter By Invoice Due Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<Match>) => {
          if (!params.data?.invoice_due_date) return 'Missing'
          return 'Attached'
        },
      }),
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.invoice_due_date) return null
        // Parse the date and normalize to midnight for consistent filtering
        const date = new Date(params.data.invoice_due_date)
        date.setHours(0, 0, 0, 0)
        return date
      },
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return 'N/A'
        return new Date(params.value).toLocaleDateString()
      },
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return <Texto appearance='hint'>N/A</Texto>
        return <Texto>{new Date(params.value).toLocaleDateString()}</Texto>
      },
    },
  ]
}
